import { Link } from "react-router-dom";
import { Button } from "../../Components/ui/button";
import { Card } from "../../Components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../Components/ui/dialog";
import { ScrollArea } from "../../Components/ui/scroll-area";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img
              src="/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png"
              alt="Measure Ignite"
              className="h-12 sm:h-14 w-auto"
            />
          </div>
          <div className="flex gap-3">
            <Link to="/login">
              <Button
                variant="outline"
                className="hidden sm:inline-flex"
              >
                Sign In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="!bg-[#FF4D00] hover:!bg-[#FF1200]">
                Join Ignite
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section with Video */}
      <section className="relative overflow-hidden bg-white">
        <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left Column - Text Content */}
            <div className="max-w-2xl lg:pt-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-gray-900">
                Track your impact. Share your work. Find your
                village.
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 leading-relaxed">
                Ignite is Measure's impact and connection
                platform for organizations doing great work,
                whether you're reporting to a foundation or
                looking to connect with others who share your
                values.
              </p>
              <p className="text-base text-gray-600 mb-8">
                Built to support human-led work and
                community-defined outcomes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto !bg-[#FF4D00] hover:!bg-[#FF1200] !text-white px-8"
                  >
                    Join Ignite
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto px-8"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column - Video */}
            <div className="w-full h-full flex justify-center items-start">
              <div className="relative mt-0 lg:mt-4 rounded-2xl overflow-hidden shadow-2xl w-full max-w-md h-[320px]">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  style={{ transform: 'scale(1.02)' }}
                  src="https://www.youtube.com/embed/NPaZyUGcJ1I?autoplay=1&mute=1&loop=1&playlist=NPaZyUGcJ1I&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
                  title="Community Powered Impact Measurement"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Ignite Is For */}
      <section className="bg-gray-50 py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-8 sm:mb-12">
            Who Ignite Is For
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">
                For Grantees
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Organize your impact data, track outcomes, and
                generate clear, foundation-ready reports,
                without extra administrative burden.
              </p>
            </Card>
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">
                For Community-Centered Organizations
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Share your work, increase visibility, and
                connect with others doing aligned work, even if
                you're not reporting to a funder.
              </p>
            </Card>
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-xl mb-3">
                For People Looking for Their Village
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Find organizations working on similar issues, in
                your region or beyond, and build relationships
                rooted in shared purpose.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How Ignite Works */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-8 sm:mb-12">
            How Ignite Works
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                num: "1",
                title: "Join Ignite",
                desc: "Create an account and set up your organization or profile.",
              },
              {
                num: "2",
                title: "Share Your Work",
                desc: "Enter impact data, outcomes, activities, or simply describe what you're building.",
              },
              {
                num: "3",
                title: "See It Clearly",
                desc: "Ignite organizes your information into a clear dashboard and profile.",
              },
              {
                num: "4",
                title: "Report or Connect",
                desc: "Generate reports for funders or connect with other organizations doing meaningful work.",
              },
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-12 h-12 bg-[#FF4D00] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="font-semibold mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Do */}
      <section className="bg-gray-50 py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Reporting Image */}
            <div className="order-2 lg:order-1">
              <img
                src="/images/67d4c57d809aa88408676c712a904166e06de4a5.png"
                alt="Person working on laptop tracking impact data"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>

            {/* Right - Reporting Text */}
            <div className="order-1 lg:order-2">
              <h3 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6">
                Reporting Made Simpler
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>Track outcomes and metrics</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Align data to grants and reporting periods
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Generate clean, foundation-ready reports
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>Export and share with confidence</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-12 lg:mt-20">
            {/* Left - Connection Text */}
            <div>
              <h3 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6">
                Connection Without Competition
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Discover organizations doing aligned work
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Increase visibility in a values-driven space
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>Build relationships, not rankings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>Find your village</span>
                </li>
              </ul>
            </div>

            {/* Right - Connection Image */}
            <div>
              <img
                src="/images/9b87ef4ffd83e0d34794e60b6222d9293b7a9e04.png"
                alt="People connecting and collaborating"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-12 lg:mt-20">
            {/* Left - Text Content */}

            <div className="order-2 lg:order-1">
              <img
                src="/images/b4c9329b79dddbfdc8f9253b1d1c466a177deb86.png"
                alt="Diverse community of leaders and organizers"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            {/* Right - Community Image */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-semibold mb-4 sm:mb-6">
                Your Data, Your Control
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Ignite is built with community accountability
                and data ethics at its core. You decide what to
                share, who sees it, and how it's used.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Control your profile visibility and privacy
                    settings
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Choose what information to share publicly or
                    keep private
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Your data is never sold or used without your
                    permission
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Built on principles of trust, transparency,
                    and community accountability
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-12 lg:mt-20">
            {/* Left - Collaboration Image */}

            <div className="">
              <h2 className="text-3xl sm:text-4xl font-semibold mb-4 sm:mb-6">
                Built for Collaboration
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Whether you're working solo or with a team,
                Ignite supports the way you actually
                work—together, with clarity and purpose.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Collaborate with your team in one shared
                    workspace
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Share insights and outcomes across your
                    organization
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Streamline reporting with your entire team
                    on the same page
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF4D00] mt-1 flex-shrink-0">
                    ✓
                  </span>
                  <span>
                    Work together, stay aligned, and amplify
                    your impact
                  </span>
                </li>
              </ul>
            </div>
            {/* Right - Text Content */}
            <div className="">
              <img
                src="/images/432d255817570aede1ead6357313af8bc41fe39a.png"
                alt="Team members collaborating in a professional setting"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How Ignite Uses AI */}
      <section className="bg-[#FFF5F0] py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold mb-6">
              How Ignite Uses AI
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Ignite uses AI only to support organizations,
              clarity, and reporting. AI helps structure
              information entered by users, assist with report
              formatting, and reduce repetitive administrative
              work.
            </p>
            <h3 className="text-xl sm:text-2xl font-semibold mb-4">
              What AI Does Not Do
            </h3>
            <ul className="text-left max-w-xl mx-auto space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-[#FF1200] mt-1 flex-shrink-0">
                  ✗
                </span>
                <span>Interpret community meaning</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#FF1200] mt-1 flex-shrink-0">
                  ✗
                </span>
                <span>Judge or score organizations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#FF1200] mt-1 flex-shrink-0">
                  ✗
                </span>
                <span>
                  Make funding or partnership decisions
                </span>
              </li>
            </ul>
            <Dialog>
              <DialogTrigger asChild>
                <button className="text-[#FF4D00] hover:text-[#FF1200] underline font-medium">
                  Learn more about how Ignite uses AI →
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh]">
                <DialogHeader className="space-y-3">
                  <DialogTitle className="text-2xl font-bold text-gray-900">
                    How Ignite Uses AI Responsibly
                  </DialogTitle>
                  <DialogDescription className="text-base text-gray-600">
                    AI supports your work—it never decides for you.
                  </DialogDescription>
                </DialogHeader>

                <ScrollArea className="max-h-[55vh] pr-4">
                  <div className="space-y-6 py-4">
                    {/* Core Principle */}
                    <div className="bg-gradient-to-r from-[#FFF5F0] to-white p-5 rounded-xl border-l-4 border-[#FF4D00]">
                      <p className="text-gray-700 leading-relaxed">
                        <strong className="text-gray-900">Measure Ignite uses AI as a supportive tool</strong> to enhance clarity, organization, and reporting, <strong className="text-[#FF4D00]">not as a decision-making authority</strong>.
                      </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* What AI Does */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                          <span className="text-[#FF4D00] text-2xl">✓</span>
                          What AI Does
                        </h4>
                        <ul className="space-y-2 text-sm">
                          {[
                            "Organize your information",
                            "Help draft reports",
                            "Assist with communications",
                            "Suggest connections",
                            "Reduce admin work"
                          ].map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-[#FF4D00] mt-1 flex-shrink-0 text-xs">•</span>
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* What AI Does NOT Do */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                          <span className="text-[#FF1200] text-2xl">✗</span>
                          What AI Does NOT Do
                        </h4>
                        <ul className="space-y-2 text-sm">
                          {[
                            "Judge or rank organizations",
                            "Make funding decisions",
                            "Interpret community meaning",
                            "Replace human judgment",
                            "Act without your approval"
                          ].map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-[#FF1200] mt-1 flex-shrink-0 font-bold">✗</span>
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Key Principles */}
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-4">
                        Our Commitments
                      </h4>
                      <div className="space-y-3">
                        {[
                          {
                            icon: "👥",
                            title: "Human Oversight",
                            desc: "Humans maintain authority over all AI outputs"
                          },
                          {
                            icon: "🔍",
                            title: "Transparency",
                            desc: "AI's role is clearly communicated"
                          },
                          {
                            icon: "🛡️",
                            title: "No Harm",
                            desc: "No profiling, surveillance, or discrimination"
                          },
                          {
                            icon: "🔒",
                            title: "Data Control",
                            desc: "Your data is never used without permission"
                          }
                        ].map((principle, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-[#FFF5F0] rounded-lg">
                            <span className="text-2xl flex-shrink-0">{principle.icon}</span>
                            <div>
                              <strong className="text-gray-900 text-sm">{principle.title}</strong>
                              <p className="text-xs text-gray-600 mt-0.5">{principle.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CARE Model Callout */}
                    <div className="bg-gradient-to-r from-[#FF4D00] to-[#E8973E] p-5 rounded-xl text-white">
                      <p className="font-semibold text-sm leading-relaxed">
                        ✨ These commitments align with Measure's <strong>CARE Model</strong>: Community, Advocacy, Resilience, and Evidence.
                      </p>
                    </div>

                    {/* Governance Footer */}
                    <div className="text-center pt-2">
                      <p className="text-xs text-gray-500 mb-3">
                        Aligned with OECD, NIST, UNESCO, and EU AI governance frameworks
                      </p>
                      <Link 
                        to="/ai-policy"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="inline-flex items-center gap-2 px-6 py-3 !bg-[#FF4D00] hover:!bg-[#FF1200] !text-white rounded-lg font-semibold transition-colors"
                      >
                        Read Complete AI Policy
                        <span>→</span>
                      </Link>
                    </div>
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Built for Trust */}
      <section className="relative bg-gradient-to-br from-[#FF4D00] via-[#E8973E] to-[#FF9E80] text-white py-12 sm:py-20 overflow-hidden">
        {/* Background Pattern Image */}
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/c857f2b21bd514015e179b3b1130ce9b59a7c724.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-semibold mb-6">
                Built for Community, Trust, and Accountability
              </h2>
              <p className="text-lg leading-relaxed mb-6">
                Ignite was created by Measure to support
                equitable evaluation, community accountability,
                and responsible use of technology. Whether
                you're reporting to a foundation or looking for
                your people, Ignite is designed to meet you
                where you are.
              </p>
              <Link to="/signup">
                <Button
                  size="lg"
                  variant="outline"
                  className="!border-white !text-[#FF4D00] hover:!bg-white hover:!text-[#FF4D00]"
                >
                  Get Started Today
                </Button>
              </Link>
            </div>

            <div className="hidden lg:block">
              <img
                src="/images/c857f2b21bd514015e179b3b1130ce9b59a7c724.png"
                alt="Person using tablet to track community impact"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4">
            Ready to get started?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            If you're doing good work and looking for clarity,
            connection, or visibility, Ignite is for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button
                size="lg"
                className="w-full sm:w-auto !bg-[#FF4D00] hover:!bg-[#FF1200] !text-white"
              >
                Join Ignite
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto !border-[#E8973E] !text-[#FF4D00] hover:!bg-[#FF9E80] hover:!text-white"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row flex-wrap justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6 text-sm text-gray-600">
              <a href="#" className="hover:text-[#FF4D00]">
                About Measure
              </a>
              <Link to="/ai-policy" className="hover:text-[#FF4D00]">
                How Ignite Uses AI
              </Link>
              <a href="#" className="hover:text-[#FF4D00]">
                Privacy & Data Ethics
              </a>
              <a href="#" className="hover:text-[#FF4D00]">
                Contact
              </a>
            </div>
            <p className="text-sm text-gray-500">
              © Measure | Ignite Platform
            </p>
          </div>
        </div>
      </footer>

      {/* Animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
